/**
 * Shared DataGrid Styles Hook
 *
 * Provides consistent Fabric-style styling for all DataGrid tables in the application.
 * Features: compact rows, white backgrounds, minimal design, colored status dots.
 */

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

export const useDataGridStyles = makeStyles({
  // Container
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Fabric-style DataGrid
  table: {
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    // Compact column headers - neutral background for clean look
    '& [role="columnheader"]': {
      fontWeight: '600',
      fontSize: '14px',
      backgroundColor: tokens.colorNeutralBackground3,
      color: tokens.colorNeutralForeground1,
      textAlign: 'left',
    },
    '& .fui-DataGridHeaderCell__button': {
      fontWeight: '600',
      fontSize: '14px',
    },
    '& .fui-DataGridHeader': {
      ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
      backgroundColor: tokens.colorNeutralBackground3,
    },
    // Compact data rows - no zebra striping
    '& .fui-DataGridBody [role="row"]': {
      minHeight: '36px',
      height: 'auto',
      backgroundColor: tokens.colorNeutralBackground1,
      transitionProperty: 'background-color',
      transitionDuration: '150ms',
      transitionTimingFunction: 'ease',
    },
    '& .fui-DataGridBody [role="row"]:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    // Row action button - hidden by default
    '& .row-action-button': {
      opacity: 0,
      transitionProperty: 'opacity',
      transitionDuration: '150ms',
      transitionTimingFunction: 'ease-in-out',
    },
    // Show action button on row hover
    '& .fui-DataGridBody [role="row"]:hover .row-action-button': {
      opacity: 1,
    },
    // Compact cell padding
    '& [role="gridcell"]': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      paddingTop: '6px',
      paddingBottom: '6px',
      fontSize: '14px',
      verticalAlign: 'middle',
      textAlign: 'left',
    },
  },
  // Toolbar area above grid
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  toolbarButton: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
  },
  searchBox: {
    width: '240px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
  },
  // Column header text
  columnHeader: {
    color: tokens.colorNeutralForeground1,
    fontSize: '14px',
    fontWeight: '600',
  },
  // Cell text
  cellText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  cellTextSecondary: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  // Pagination footer - neutral background to match header
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground3,
  },
  paginationInfo: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  pageText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  // Status indicators - colored dots
  statusDot: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    display: 'inline-block',
    marginRight: '8px',
  },
  statusActive: {
    backgroundColor: tokens.colorStatusSuccessForeground1,
  },
  statusPending: {
    backgroundColor: tokens.colorStatusWarningForeground1,
  },
  statusInactive: {
    backgroundColor: tokens.colorStatusDangerForeground1,
  },
  statusText: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
});
